/**
 * useEventLogHandlers - Socket-Handler für das Event-Log System
 *
 * Registriert Listener für 'gameEvent' und fügt Events zum EventLog hinzu.
 * Triggert Toast-Benachrichtigungen für betroffene Spieler bei Effekten.
 */

import { useCallback, useEffect } from 'react';
import socketService from '../socketService';
import { useEventLog } from '../contexts/EventLogContext';
import { useNotifications } from '../components/notifications';
import soundService from '../soundService';
import { GAME_EVENT, EFFECT_UNIT } from '../core/eventTypes';
import { NOTIFICATIONS } from '../config/animationTimings';

/**
 * Erstellt eine Toast-Nachricht für einen Effekt
 * @returns {{ icon: string, message: string } | null}
 */
function getToastForEffect(effect) {
  const { value, unit, effectName, effectIcon } = effect;
  if (!unit || value === undefined || value === null || value === 0) return null;

  const sign = value > 0 ? '+' : '';
  const prefix = effectName ? `${effectName}: ` : '';

  switch (unit) {
    case EFFECT_UNIT.POINTS:
      return { icon: effectIcon || '⭐', message: `${prefix}${sign}${value}⭐` };
    case EFFECT_UNIT.COINS:
      return { icon: effectIcon || '💰', message: `${prefix}${sign}${value}💰` };
    case EFFECT_UNIT.MULT:
      return { icon: effectIcon || '✖️', message: `${prefix}${sign}${value}✖️` };
    case EFFECT_UNIT.CARDS:
      return {
        icon: effectIcon || '🃏',
        message: value > 0
          ? `${prefix}+${value} Karte${value > 1 ? 'n' : ''}`
          : `${prefix}${value} Karte${Math.abs(value) > 1 ? 'n' : ''}`
      };
    default:
      return null;
  }
}

/**
 * Hook zum Verarbeiten von gameEvent Socket-Events
 *
 * @param {Object} config - Konfiguration
 * @param {number} config.activePlayerIndex - Index des aktuell aktiven/anzeigenden Spielers
 */
export function useEventLogHandlers(config = {}) {
  const { activePlayerIndex = 0, isTestMode = false } = config;
  const { addEvent, clearLog } = useEventLog();
  const { notify } = useNotifications();

  /**
   * Triggert Toasts für Effekte die den aktiven Spieler betreffen
   */
  const triggerEffectToasts = useCallback((effects, isMe) => {
    if (!effects || effects.length === 0 || !isMe) return;

    effects.forEach(effect => {
      const toastData = getToastForEffect(effect);
      if (toastData) {
        notify(toastData.message, {
          icon: toastData.icon,
          priority: 'normal',
          duration: NOTIFICATIONS.effectToast
        });
      }
    });
  }, [notify]);

  /**
   * Handler für gameEvent Socket-Events
   * Ergänzt das Event um isMe-Flag und fügt es zum Log hinzu
   */
  const handleGameEvent = useCallback((event) => {
    if (!event || !event.id) {
      console.warn('[EventLog] Received invalid event:', event);
      return;
    }

    // Bestimme ob dieses Event "mich" betrifft
    // Im Test-Mode: Immer true (da wir alle Spieler kontrollieren)
    // Im Multiplayer: Nur wenn es unser Spieler ist
    let isMe = false;

    if (isTestMode) {
      // Test-Mode: Zeige Toasts für alle Spieler
      isMe = event.playerIndex >= 0 || event.mainEvent?.playerIndex >= 0;
    } else if (event.type === 'group') {
      // Gruppiertes Event: Check mainEvent.playerIndex
      isMe = event.mainEvent?.playerIndex === activePlayerIndex;
    } else if (event.type === 'single') {
      // Einzelnes Event: Check playerIndex direkt
      // System-Events (playerIndex === -1) haben kein isMe
      isMe = event.playerIndex >= 0 && event.playerIndex === activePlayerIndex;
    }

    // Event mit isMe-Flag ergänzen
    const enrichedEvent = {
      ...event,
      isMe
    };

    // Zum Log hinzufügen
    addEvent(enrichedEvent);

    // Sound für Joker/Siegel-Aktivierungen spielen (für alle Spieler)
    // Gruppiertes Event mit Joker-Effekten
    if (event.type === 'group' && event.jokerEffects?.length > 0) {
      soundService.play('joker_activate');
    }
    // Gruppiertes Event mit Siegel-Effekten
    if (event.type === 'group' && event.sealEffects?.length > 0) {
      soundService.play('seal_trigger');
    }
    // INSTANT-Siegel Event (aber nicht für passive income - das hat einen eigenen leisen Münz-Sound)
    if (event.eventId === GAME_EVENT.SEAL_INSTANT && event.effectType !== 'passiveIncome') {
      soundService.play('seal_trigger');
    }
    // Zauberkarte gezogen: Bling-Sound wenn Event im Log erscheint
    if (event.eventId === GAME_EVENT.MAGIC_CARD_DRAWN) {
      soundService.play('joker_bonus');
    }
    // Minispiel-Belohnung: Bling-Sound für Punkte/Münzen
    if (event.eventId === GAME_EVENT.MINIGAME_REWARD) {
      soundService.play('joker_bonus2');
    }

    // === TOAST-BENACHRICHTIGUNGEN für betroffene Spieler ===
    if (event.type === 'group' && isMe) {
      // Basis-Punkte vom Stichgewinn -> Toast
      const basePoints = event.mainEvent?.data?.basePoints;
      if (basePoints > 0) {
        const trickName = event.mainEvent.data.trickName || 'Stich';
        notify(`${trickName}: +${basePoints}⭐`, {
          icon: '🏆',
          priority: 'normal',
          duration: NOTIFICATIONS.effectToast
        });
      }
      // Siegel-Effekte -> Toasts
      triggerEffectToasts(event.sealEffects, isMe);
      // Joker-Effekte -> Toasts
      triggerEffectToasts(event.jokerEffects, isMe);
    }
    // INSTANT-Siegel: single event mit selfValue/selfUnit
    else if (event.eventId === GAME_EVENT.SEAL_INSTANT && isMe && event.data?.selfValue) {
      const instantEffect = {
        effectId: event.data.sealId,
        effectName: event.data.sealName,
        effectIcon: event.data.sealIcon,
        value: event.data.selfValue,
        unit: event.data.selfUnit
      };
      triggerEffectToasts([instantEffect], isMe);
    }
  }, [activePlayerIndex, isTestMode, addEvent, notify, triggerEffectToasts]);

  /**
   * Handler für neues Spiel - löscht das Log komplett
   */
  const handleGameStarted = useCallback(() => {
    clearLog();
  }, [clearLog]);

  /**
   * Registriert die Event-Listener
   */
  const registerEventLogListeners = useCallback(() => {
    socketService.registerListener('gameEvent', handleGameEvent);
    // Bei neuem Spiel: Log komplett löschen
    socketService.registerListener('gameStarted', handleGameStarted);
  }, [handleGameEvent, handleGameStarted]);

  /**
   * Entfernt die Event-Listener
   */
  const unregisterEventLogListeners = useCallback(() => {
    socketService.off('gameEvent');
    socketService.off('gameStarted');
  }, []);

  return {
    registerEventLogListeners,
    unregisterEventLogListeners,
    handleGameEvent, // Exposed for testing
  };
}

/**
 * Hook der automatisch Listener registriert/entfernt
 * Für einfache Verwendung in Komponenten
 */
export function useEventLogAutoRegister(config = {}) {
  const {
    registerEventLogListeners,
    unregisterEventLogListeners
  } = useEventLogHandlers(config);

  useEffect(() => {
    registerEventLogListeners();
    return () => {
      unregisterEventLogListeners();
    };
  }, [registerEventLogListeners, unregisterEventLogListeners]);
}

export default useEventLogHandlers;
